import React, { memo, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBox from '@mui/icons-material/IndeterminateCheckBox';
import Search from '@mui/icons-material/Search';
import type { SwaggerOperation } from '../../types/mappingDocument';
import { monoSx } from '../dashboard/styles';
import MethodBadge from './MethodBadge';
import { searchOperations } from './mappingScope';

const rowGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '24px 64px minmax(0,1fr)', sm: '24px 64px minmax(0,1.2fr) minmax(0,1fr)' },
  gap: 1.25,
  alignItems: 'center',
} as const;

interface OperationRowProps {
  operation: SwaggerOperation;
  selected: boolean;
  onToggle: (operationId: string) => void;
}

const OperationRow = memo<OperationRowProps>(({ operation, selected, onToggle }) => {
  const Icon = selected ? CheckBox : CheckBoxOutlineBlank;
  return (
    <ButtonBase
      role="checkbox"
      aria-checked={selected}
      aria-label={operation.operationId}
      onClick={() => onToggle(operation.operationId)}
      sx={(theme) => ({
        ...rowGridSx,
        width: '100%',
        minHeight: 48,
        px: 1.5,
        py: 0.75,
        textAlign: 'left',
        borderBottom: 1,
        borderColor: 'action.hover',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
        contentVisibility: 'auto',
        containIntrinsicSize: 'auto 48px',
        '&:hover': { bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : 'action.hover' },
        '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
      })}
    >
      <Icon sx={{ fontSize: 20, color: selected ? 'primary.dark' : 'text.secondary' }} />
      <MethodBadge method={operation.method} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
        <Typography noWrap sx={{ ...monoSx, fontSize: 12, fontWeight: 500 }} title={operation.operationId}>
          {operation.operationId}
        </Typography>
        {operation.summary && (
          <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }} title={operation.summary}>
            {operation.summary}
          </Typography>
        )}
      </Box>
      <Typography noWrap sx={{ ...monoSx, display: { xs: 'none', sm: 'block' }, fontSize: 11, color: 'text.secondary' }} title={operation.path}>
        {operation.path}
      </Typography>
    </ButtonBase>
  );
});
OperationRow.displayName = 'OperationRow';

interface OperationListProps {
  /** Operations in the current tag scope. */
  operations: SwaggerOperation[];
  picks: ReadonlySet<string>;
  onToggle: (operationId: string) => void;
  /** Add (`selected`) or remove the given operation IDs from the picks. */
  onSetMany: (operationIds: string[], selected: boolean) => void;
}

/** Searchable checklist of operations with "select shown". */
const OperationList: React.FC<OperationListProps> = ({ operations, picks, onToggle, onSetMany }) => {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => searchOperations(operations, query), [operations, query]);

  const visibleSelected = visible.reduce((n, op) => n + (picks.has(op.operationId) ? 1 : 0), 0);
  const allVisible = visible.length > 0 && visibleSelected === visible.length;
  const BulkIcon = allVisible ? CheckBox : visibleSelected ? IndeterminateCheckBox : CheckBoxOutlineBlank;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, px: 1.5, py: 1.25, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>Operations</Typography>
        <Box
          sx={{
            flex: '1 1 220px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 36,
            px: 1.5,
            borderRadius: 4.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '&:focus-within': { borderColor: 'primary.dark' },
          }}
        >
          <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
          <InputBase
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search operation ID or path"
            inputProps={{ 'aria-label': 'Search operations' }}
            sx={{ fontSize: 13 }}
          />
        </Box>
        <ButtonBase
          disabled={visible.length === 0}
          onClick={() => onSetMany(visible.map((op) => op.operationId), !allVisible)}
          sx={{ height: 32, pl: 0.75, pr: 1.25, gap: 0.75, borderRadius: 2, fontSize: 12, fontWeight: 600, color: 'text.secondary', '&:hover': { bgcolor: 'background.paper' }, '&.Mui-disabled': { opacity: 0.5 } }}
        >
          <BulkIcon sx={{ fontSize: 20, color: visibleSelected ? 'primary.dark' : 'text.secondary' }} />
          {allVisible ? 'Deselect shown' : `Select ${visible.length} shown`}
        </ButtonBase>
      </Box>
      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {visible.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>
            {operations.length === 0 ? 'No operations in this spec' : 'No operations match'}
          </Typography>
        ) : (
          visible.map((op) => (
            <OperationRow key={`${op.method} ${op.path}`} operation={op} selected={picks.has(op.operationId)} onToggle={onToggle} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default memo(OperationList);
